import type { MorrowAgent } from "@t3tools/client-runtime/state/morrowAgentRuntime";
import { morrowAgentSummary } from "@t3tools/client-runtime/state/morrowAgentRuntime";

import type { Translator } from "~/i18n";

import { localizeToolCallDescription } from "./components/chat/toolCallDescriptionI18n";
import { describeToolCall } from "./components/chat/workEntryStep";

/** Human-readable one-line summary for a MorrowOS sub-agent card. */
export function formatMorrowAgentSummary(agent: MorrowAgent, t?: Translator): string {
  if (agent.status === "running" && agent.currentToolName) {
    const raw = describeToolCall(agent.currentToolName);
    const desc = t ? localizeToolCallDescription(raw, agent.currentToolName, t) : raw;
    if (desc.appName) {
      const action = desc.action.length > 0 ? desc.action : t ? t("chat.tool.working") : "Working";
      return `${desc.appName} · ${action}…`;
    }
    if (desc.action.length > 0) {
      return `${desc.action}…`;
    }
    return t ? `${t("chat.tool.working")}…` : "Working…";
  }
  return morrowAgentSummary(agent);
}

/** Logo URL for a tool call — Morrow-native tools use null (caller shows mascot). */
export function toolCallLogoUrl(toolName: string): string | null {
  const desc = describeToolCall(toolName);
  if (
    desc.slug === "composio_search" ||
    desc.slug === "spawn_subagent" ||
    desc.appName === "Morrow"
  ) {
    return null;
  }
  return desc.logoUrl;
}

/** True when the tool is a Morrow-native action (show mascot instead of app logo). */
export function isMorrowNativeTool(toolName: string): boolean {
  const desc = describeToolCall(toolName);
  return (
    desc.slug === "composio_search" || desc.slug === "spawn_subagent" || desc.appName === "Morrow"
  );
}

// A friendly, stable name for a sub-agent instead of the raw `subagent-2dea4c0a`
// id. Deterministic from the id, so the same sub-agent always reads the same.
const CUTE_ADJECTIVES = [
  "Swift",
  "Calm",
  "Clever",
  "Bright",
  "Brave",
  "Gentle",
  "Merry",
  "Nimble",
  "Sunny",
  "Cosmic",
  "Lucky",
  "Cozy",
  "Plucky",
  "Zesty",
  "Mellow",
  "Chipper",
  "Dapper",
  "Jolly",
  "Snappy",
  "Breezy",
] as const;
const CUTE_CRITTERS = [
  "Otter",
  "Falcon",
  "Fox",
  "Panda",
  "Robin",
  "Koala",
  "Lynx",
  "Sparrow",
  "Hedgehog",
  "Badger",
  "Heron",
  "Marmot",
  "Wren",
  "Ferret",
  "Quokka",
  "Puffin",
  "Pika",
  "Tapir",
  "Finch",
  "Comet",
] as const;

function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) h = (h * 31 + input.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Cute, stable display name for a sub-agent id (e.g. "Swift Otter"). */
export function cuteAgentName(agentId: string): string {
  const h = hashString(agentId);
  const adjective = CUTE_ADJECTIVES[h % CUTE_ADJECTIVES.length];
  const critter = CUTE_CRITTERS[(h >> 5) % CUTE_CRITTERS.length];
  return `${adjective} ${critter}`;
}
